import { create } from 'zustand';

/** localStorage key はテナント別 namespace (stores/scope.ts の scopeStorageKey と同じパターン)。 */
const scopePinStorageKey = (tenantId: string) => `misespark:scope-pin:${tenantId}`;

// 実装レビューMEDIUM (QA): 月部分を 01〜12 に絞る (単純な \d{2} だと '2026-13-01' 等の
// 改ざん/バグ値も通過してしまう)
const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-01$/;

type PersistedScopePin = {
  isPinned: boolean;
  pinnedPeriodStart: string | null;
  pinnedPeriodEnd: string | null;
};

const EMPTY_PIN: PersistedScopePin = { isPinned: false, pinnedPeriodStart: null, pinnedPeriodEnd: null };

/** 壊れたJSON・旧形式・localStorage改ざんへの耐性 (stores/scope.ts の loadRecentNodeIds と同水準)。
 *  不正な形なら固定なしの既定値へフォールバックする。 */
const loadPersistedScopePin = (tenantId: string): PersistedScopePin => {
  try {
    const raw = localStorage.getItem(scopePinStorageKey(tenantId));
    if (!raw) return EMPTY_PIN;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_PIN;
    const { isPinned, pinnedPeriodStart, pinnedPeriodEnd } = parsed as Record<string, unknown>;
    if (typeof isPinned !== 'boolean') return EMPTY_PIN;
    if (pinnedPeriodStart !== null && (typeof pinnedPeriodStart !== 'string' || !MONTH_PATTERN.test(pinnedPeriodStart))) return EMPTY_PIN;
    if (pinnedPeriodEnd !== null && (typeof pinnedPeriodEnd !== 'string' || !MONTH_PATTERN.test(pinnedPeriodEnd))) return EMPTY_PIN;
    if (isPinned && (!pinnedPeriodStart || !pinnedPeriodEnd || pinnedPeriodStart > pinnedPeriodEnd)) return EMPTY_PIN;
    return { isPinned, pinnedPeriodStart: pinnedPeriodStart as string | null, pinnedPeriodEnd: pinnedPeriodEnd as string | null };
  } catch {
    return EMPTY_PIN; // Private Mode / QuotaExceeded / 破損JSON
  }
};

const persistScopePin = (tenantId: string, value: PersistedScopePin) => {
  try {
    localStorage.setItem(scopePinStorageKey(tenantId), JSON.stringify(value));
  } catch {
    // 保存できなくても選択状態はメモリ上の state には反映済みなので致命的ではない
  }
};

type ScopePinState = PersistedScopePin & {
  tenantId: string | null;
  /** 初回ログイン時・テナント切替時に呼ぶ。localStorageから復元する。 */
  loadForTenant: (tenantId: string) => void;
  /** 'YYYY-MM-01' 形式の開始月・終了月ペアを固定する。periodStart<=periodEnd の検証は
   *  呼び出し元 (ScopePinToggle) の責務とする (CLAUDE.md §6、不要なerror handlingを追加しない)。 */
  pin: (tenantId: string, periodStart: string, periodEnd: string) => void;
  /** 固定を解除する。pinnedPeriodStart/pinnedPeriodEnd の値自体は消さない
   *  (次回「固定する」を押した時のポップオーバー初期値に使うため)。 */
  unpin: (tenantId: string) => void;
};

export const useScopePinStore = create<ScopePinState>((set) => ({
  ...EMPTY_PIN,
  tenantId: null,
  loadForTenant: (tenantId) => set({ ...loadPersistedScopePin(tenantId), tenantId }),
  pin: (tenantId, periodStart, periodEnd) => {
    const next: PersistedScopePin = { isPinned: true, pinnedPeriodStart: periodStart, pinnedPeriodEnd: periodEnd };
    persistScopePin(tenantId, next);
    set({ ...next, tenantId });
  },
  unpin: (tenantId) => set((s) => {
    // 実装レビューMEDIUM (design-and-code): storeState全体をspreadすると関数やtenantIdまで
    // 巻き込む。pin()と同様にPersistedScopePinの3フィールドのみ明示構築する。
    const next: PersistedScopePin = { isPinned: false, pinnedPeriodStart: s.pinnedPeriodStart, pinnedPeriodEnd: s.pinnedPeriodEnd };
    persistScopePin(tenantId, next);
    return { isPinned: false, tenantId };
  }),
}));
